import { useCallback, useEffect, useMemo, useState } from "react";
import { TimelineProps, ButtonProps, Flex, Button, Timeline } from "antd";
import { SafeAccess } from "types/_utils.types";

export type MutationKey = boolean | string | number;

/**
 * Make sure windowSize is never greater than maxCapacity
 */
export interface CroppedTimelineProps<
  T extends { key: string | number | undefined } = { key: string },
> extends TimelineProps {
  data: T[];
  itemRenderer: (item: T) => NonNullable<TimelineProps["items"]>[number];
  windowSize?: number;
  maxCapacity?: number;
  select?: "top" | "bottom";
  rootClassName?: string;
  beforeButtonText?: string;
  beforeButtonProps?: ButtonProps;
  afterButtonText?: string;
  afterButtonProps?: ButtonProps;
  mutationProps?: {
    mutationKey: MutationKey;
    onCancel: () => void;
    onConfirm: (item: Partial<Omit<T, "key">>) => void;
    renderer: (
      item: T | undefined,
      extra: { onCancel: () => void; onConfirm: (item: Partial<Omit<T, "key">>) => void },
    ) => NonNullable<TimelineProps["items"]>[number];
  };
}

export function CroppedTimelineProps<T extends { key: string | number | undefined }>({
  data,
  itemRenderer,
  windowSize = 5,
  maxCapacity,
  select = "bottom",
  rootClassName,
  beforeButtonProps,
  beforeButtonText,
  afterButtonProps,
  afterButtonText,
  mutationProps,
  ...props
}: Readonly<CroppedTimelineProps<T>>) {
  const cap = maxCapacity || windowSize * 3;
  const initialRange = useMemo(() => {
    let start: number, end: number;
    if (select === "bottom") {
      end = data.length;
      start = Math.max(0, end - cap);
    } else {
      start = 0;
      end = Math.min(cap, data.length);
    }

    return [start, end] as const;
  }, [data.length, select, cap]);

  const [range, setRange] = useState<Readonly<[number, number]>>(initialRange);
  const loadBefore = () => {
    setRange(([start, end]) => {
      const updatedStart = Math.max(0, start - windowSize);
      const updatedDiff = end - updatedStart;
      const updatedEnd = updatedStart + Math.min(updatedDiff, cap);
      return [updatedStart, updatedEnd];
    });
    mutationProps?.onCancel();
  };

  const loadAfter = () => {
    setRange(([start, end]) => {
      const updatedEnd = Math.min(end + windowSize, data.length);
      const updatedDiff = updatedEnd - start;
      const updatedStart = updatedEnd - Math.min(updatedDiff, cap);
      return [updatedStart, updatedEnd];
    });
  };

  const showLoadBefore = useMemo(() => range[0] !== 0, [range]);
  const showLoadAfter = useMemo(() => range[1] !== data.length, [range]);

  const getMutationPosition = useCallback(
    (mutationKey: SafeAccess<CroppedTimelineProps<T>, ["mutationProps", "mutationKey"]>) => {
      if (mutationKey === true) {
        return data.length;
      }

      const index = data.findIndex((val) => val.key === mutationKey);
      return index === -1 ? null : index;
    },
    [data],
  );

  const focusMutation = useCallback(
    (mutationKey: SafeAccess<CroppedTimelineProps<T>, ["mutationProps", "mutationKey"]>) => {
      if (mutationKey === true) {
        const pos = data.length;
        setRange([Math.max(0, pos - cap), pos]);

        return pos;
      } else {
        const pos = data.findIndex((val) => val.key === mutationKey) + 1;
        if (pos) {
          const startFrom = pos - cap;
          const adjust = Math.max(0, -startFrom);
          setRange([startFrom + adjust, Math.min(pos + adjust, data.length)]);
        }

        return pos - 1;
      }
    },
    [data, cap],
  );

  useEffect(() => {
    if (mutationProps?.mutationKey) focusMutation(mutationProps.mutationKey);
  }, [mutationProps?.mutationKey, data]);

  const items = useMemo(() => {
    const existingItems = data.slice(...range).map(itemRenderer);
    if (mutationProps?.mutationKey) {
      const pos = getMutationPosition(mutationProps.mutationKey);
      if (pos && pos >= data.length) {
        existingItems.push(mutationProps.renderer(undefined, mutationProps));
      } else if (pos !== null) {
        existingItems[pos - range[0]] = mutationProps.renderer(data[pos], mutationProps);
      }
    }

    return existingItems;
  }, [data, range, mutationProps?.mutationKey]);

  return (
    <Flex vertical gap={12} className={rootClassName}>
      {showLoadBefore && (
        <Button {...beforeButtonProps} onClick={loadBefore}>
          {beforeButtonText || "Load earlier"}
        </Button>
      )}
      <Timeline items={items} {...props} />
      {showLoadAfter && (
        <Button {...afterButtonProps} onClick={loadAfter}>
          {afterButtonText || "Load later"}
        </Button>
      )}
    </Flex>
  );
}

export default CroppedTimelineProps;
